import { BuildClass } from '../../../../universal';
import { React, _ } from '../../../lib';
import { isDarkTheme } from '../../component-main';
import { startGlobalVoiceRecording } from '../voice-recorder';
import { FormRow } from '../wrappers';
import { runLLMFormFill } from './llm-fill';
import { readDynamic, } from './types';
export var FormSaveOutcome;
(function (FormSaveOutcome) {
    FormSaveOutcome[FormSaveOutcome["Good"] = 1] = "Good";
    FormSaveOutcome[FormSaveOutcome["Neutral"] = 2] = "Neutral";
    FormSaveOutcome[FormSaveOutcome["Error"] = 3] = "Error";
})(FormSaveOutcome || (FormSaveOutcome = {}));
const theme = {
    bgChangedGood: () => (isDarkTheme() ? '!bg-[#040]' : '!bg-[#afa]'),
    bgChangedBad: () => (isDarkTheme() ? '!bg-[#500]' : '!bg-[#faa]'),
};
export class FormManager {
    fields;
    formFixer;
    disableFormFill;
    defaultState;
    constructor(input) {
        this.fields = input.fields;
        this.formFixer = input.formFixer;
        this.defaultState = this.getDefaultState(input.getDefaultValues);
        const realUserID = window.rootData.Session.RealUser;
        this.disableFormFill =
            input.disableFormFill ?? (realUserID == null || realUserID > 100000);
    }
    getDefaultState(getDefaultValues) {
        const defVals = getDefaultValues?.() ?? {};
        return _.fromPairs(_.map(this.fields, (x, k) => {
            const toInternalFn = x.typeMap?.toInternal ?? (a => a);
            const defV = defVals[k];
            const defFn = x.def ?? (defV ? () => defV : x.valueDefaults.def);
            const v = runFixerFull(x, toInternalFn(defFn()));
            return [k, v];
        }));
    }
    getPublicState(internalState) {
        return _.fromPairs(_.map(this.fields, (F, k) => {
            const fn = F.typeMap?.toPublic ?? (x => x);
            return [k, fn(internalState[k])];
        }));
    }
    getValidationErrors(currentState) {
        return _.compact(_.flatMap(this.fields, (_x, k) => this.getValidationError(k, currentState[k])));
    }
    getValidationError(field, value) {
        const x = this.fields[field];
        if (!x) {
            return [];
        }
        return _.compact([...(x.validators ?? []), ...(x.valueDefaults.validators ?? [])].map(v => {
            if (!v) {
                return null;
            }
            if (!v.req(value)) {
                return `Invalid ${x.lbl}: ${v.msg}`;
            }
            return null;
        }));
    }
    updateValue(input) {
        if (input.depth > 10) {
            console.error({ input });
            throw new Error('Form fixer recursion limit exceeded');
        }
        const field = this.fields[input.key];
        const fieldFixer = input.fullFieldFix ? runFixerFull : runFixerImmediate;
        const new_state = {
            ...input.currentState,
            [input.key]: fieldFixer(field, input.value),
        };
        const finalState = {
            ...new_state,
            ...(this.formFixer?.(this.getPublicState(input.currentState), this.getPublicState(new_state), input.key) ?? {}),
        };
        const diffState = _.pickBy(finalState, (value, key) => !_.isEqual(value, new_state[key]));
        const second_tier_diffs = _.fromPairs(_.flatMap(diffState, (v, k) => _.toPairs(this.updateValue({
            currentState: new_state,
            key: k,
            value: v,
            fullFieldFix: input.fullFieldFix,
            depth: input.depth + 1,
        }))));
        return {
            ...finalState,
            ...second_tier_diffs,
        };
    }
    renderComponentFormRow(input) {
        const field = this.fields[input.key];
        const removeLabel = field.removeLabel ?? false;
        const hasChanged = !_.isEqual(this.defaultState[input.key], input.currentState[input.key]);
        const errors = this.getValidationError(input.key, input.currentState[input.key]);
        return (React.createElement(FormRow, { lbl: field.emptyLabel
                ? ''
                : readDynamic(field.lbl, input.currentState), className: BuildClass({
                [readDynamic(field.className, input.currentState) ?? '']: true,
                [theme.bgChangedGood()]: input.highlightChanges && hasChanged && errors.length == 0,
                [theme.bgChangedBad()]: hasChanged && errors.length > 0,
            }), lblWidth: removeLabel ? 0 : input.lblWidth, title: _.compact([
                readDynamic(field.tooltip, input.currentState),
                errors.join(', '),
            ]).join(' - '), showHelp: readDynamic(field.helpIcon, input.currentState), key: input.key }, this.renderComponent({
            ...input,
            highlightChanges: false,
            highlight: false,
            errorTooltips: false,
        })));
    }
    renderComponent(input) {
        const key = input.key;
        const field = this.fields[key];
        const highlighting = input.highlight ?? true;
        const showErrorTooltips = input.errorTooltips ?? true;
        const hasChanged = highlighting &&
            !_.isEqual(this.defaultState[input.key], input.currentState[input.key]);
        const errors = highlighting
            ? this.getValidationError(input.key, input.currentState[input.key])
            : [];
        return field.jsx({
            lbl: readDynamic(field.lbl, input.currentState),
            value: input.currentState[key],
            className: BuildClass({
                [theme.bgChangedGood()]: input.highlightChanges && hasChanged && errors.length == 0,
                [theme.bgChangedBad()]: hasChanged && errors.length > 0,
            }),
            onUpdate: v => {
                input.onUpdate(s => this.updateValue({
                    currentState: s,
                    key: key,
                    value: v,
                    fullFieldFix: false,
                    depth: 0,
                }));
            },
            disabled: readDynamic(field.disabled, input.currentState) ?? false,
            readOnly: readDynamic(field.readOnly, input.currentState) ?? false,
            title: showErrorTooltips ? errors.join(', ') : null,
            onFocus: () => {
                input.onFocus?.();
            },
            onBlur: () => {
                input.onUpdate(s => this.updateValue({
                    currentState: s,
                    key: key,
                    value: s[key],
                    fullFieldFix: true,
                    depth: 0,
                }));
                input.onBlur?.();
            },
        }, input.currentState);
    }
    async trySaving(input) {
        return new Promise(resolve => {
            const newState = _.fromPairs(_.map(this.fields, (x, k) => {
                const v = runFixerFull(x, input.currentState[k]);
                return [k, v];
            }));
            input.setState(newState);
            if (!input.allowSavingWithoutChanges &&
                _.isEqual(this.defaultState, newState)) {
                resolve({
                    status: FormSaveOutcome.Neutral,
                    message: 'Nothing has changed',
                });
                return;
            }
            const errors = this.getValidationErrors(newState);
            if (errors.length > 0) {
                resolve({
                    status: FormSaveOutcome.Error,
                    message: errors.join('\n'),
                });
                return;
            }
            const currentState = this.getPublicState(newState);
            const originalState = this.getPublicState(this.defaultState);
            const deltaState = _.pickBy(currentState, (v, k) => !_.isEqual(v, originalState[k]));
            input.onSave({
                current: currentState,
                original: originalState,
                delta: deltaState,
            }, result => {
                if (result !== true) {
                    resolve({
                        status: FormSaveOutcome.Error,
                        message: result,
                    });
                    return;
                }
                resolve({
                    status: FormSaveOutcome.Good,
                    message: '',
                });
            });
        });
    }
    startTranscriptionFormFill(input) {
        if (this.disableFormFill) {
            console.log('Form fill is disabled');
            return;
        }
        startGlobalVoiceRecording({
            editBeforeSending: input.editBeforeSending,
            prompt: input.voicePrompt,
            originatingFormElement: input.containingElement,
            onTranscribed: (requestText, callback) => {
                console.log(requestText);
                runLLMFormFill({
                    currentState: input.currentState,
                    manager: this,
                    description: input.formPrompt,
                    request: requestText,
                    yes: values => {
                        console.log({ values });
                        input.setState({
                            ...input.currentState,
                            ..._.mapValues(values, (v, k) => {
                                const old_val = input.currentState[k];
                                const llmInfo = readDynamic(this.fields[k]?.llmInfo, input.currentState);
                                const reversal_fn = llmInfo?.reversal;
                                const new_val = reversal_fn ? reversal_fn(v, old_val) : v;
                                return runFixerFull(this.fields[k], new_val);
                            }),
                        });
                        callback(true);
                    },
                });
            },
        });
    }
    hotkeyPress(event, input) {
        if (event.code == 'ControlRight' && !event.repeat && !this.disableFormFill) {
            console.log('Starting voice input');
            event.stopPropagation();
            this.startTranscriptionFormFill({
                currentState: input.currentState,
                editBeforeSending: false,
                formPrompt: input.formPrompt,
                voicePrompt: input.voicePrompt,
                setState: input.setState,
                containingElement: input.containingElement,
            });
        }
    }
    hotkeyRelease(event) {
        if (event.code == 'ControlRight') {
            event.stopPropagation();
        }
    }
}
export const runFixerFull = (field, value) => {
    if (!field) {
        return value;
    }
    const v1 = runFixerImmediate(field, value);
    const v2 = field.fixer?.(v1) ?? v1;
    const v3 = field.valueDefaults.fixer?.(v2) ?? v2;
    return runFixerImmediate(field, v3);
};
export const runFixerImmediate = (field, value) => {
    if (!field) {
        return value;
    }
    const v1 = field.fixerImmediate?.(value) ?? value;
    return field.valueDefaults.fixerImmediate?.(v1) ?? v1;
};
//# sourceMappingURL=manager.js.map